<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Play, RefreshCw, RotateCcw, Search } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, Modal, PageHeader, Pagination, Section, Select, Spinner, Table } from '@drama/ui'

import { getNovelMediaContent, listNovelMedia } from '../../api/novel/media.js'
import { useNovelPageText } from '../../components/novel/novel-page-text.js'

defineOptions({ name: 'NovelSavedMedia' })

const { t, te, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { apiError } = useNovelPageText()
const tr = (key, params = {}) => t(`novelMedia.${key}`, params)
const allowed_kinds = new Set(['speech', 'opening_speech', 'drama_video'])

const filters = reactive({ kind: '', pack_id: '', run_id: '', user_id: '' })
const loading = ref(false)
const error = ref(null)
const items = ref([])
const total = ref(0)
const limit = ref(20)
const offset = ref(0)
const preview_open = ref(false)
const preview_item = ref(null)
const preview_loading = ref(false)
const preview_error = ref(null)
const preview_url = ref('')
let list_version = 0
let preview_version = 0

const kind_options = computed(() => [
  { value: '', label: tr('filters.allKinds') },
  ...[...allowed_kinds].map(kind => ({ value: kind, label: tr(`kinds.${kind}`) })),
])
const columns = computed(() => [
  { key: 'kind', label: tr('columns.kind'), width: '170px' },
  { key: 'pack', label: tr('columns.story'), width: '230px', wrap: true },
  { key: 'owner', label: tr('columns.owner'), width: '230px', wrap: true },
  { key: 'segment', label: tr('columns.segment'), width: '165px', wrap: true },
  { key: 'created_at', label: tr('columns.created'), width: '160px' },
  { key: 'size', label: tr('columns.size'), width: '130px' },
  { key: 'actions', label: tr('columns.actions'), width: '110px', fixed: 'right' },
])
const page = computed(() => Math.floor(offset.value / limit.value) + 1)
const pages = computed(() => Math.max(1, Math.ceil(total.value / limit.value)))
const summary = computed(() => tr('pager.summary', { total: total.value, page: page.value, pages: pages.value }))

function query_text(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function current_query() {
  const query = route.query
  const requested_limit = Number(query.limit)
  const requested_offset = Number(query.offset)
  return {
    kind: allowed_kinds.has(query_text(query.kind)) ? query_text(query.kind) : undefined,
    pack_id: query_text(query.pack_id) || undefined,
    run_id: query_text(query.run_id) || undefined,
    user_id: query_text(query.user_id) || undefined,
    limit: [20, 50, 100].includes(requested_limit) ? requested_limit : 20,
    offset: Number.isSafeInteger(requested_offset) && requested_offset >= 0 ? requested_offset : 0,
  }
}

async function load() {
  const version = ++list_version
  close_preview()
  loading.value = true
  error.value = null
  try {
    const data = await listNovelMedia(current_query())
    if (version !== list_version) return
    items.value = data.items.map(item => ({ ...item, row_id: `${item.kind}:${item.asset_id}` }))
    total.value = data.total
    limit.value = data.limit
    offset.value = data.offset
  } catch (request_error) {
    if (version === list_version) error.value = apiError(request_error)
  } finally {
    if (version === list_version) loading.value = false
  }
}

watch(() => route.query, () => {
  const query = current_query()
  for (const key of Object.keys(filters)) filters[key] = query[key] ?? ''
  limit.value = query.limit
  offset.value = query.offset
  void load()
}, { immediate: true })

async function update_query(query) {
  const values = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== ''))
  const target = { path: '/novel/media', query: values }
  if (router.resolve(target).fullPath === route.fullPath) await load()
  else await router.replace(target)
}

function apply_filters() {
  void update_query({ ...filters, pack_id: filters.pack_id.trim(), run_id: filters.run_id.trim(), user_id: filters.user_id.trim(), limit: limit.value })
}

function reset_filters() {
  void update_query({ limit: limit.value })
}

function select_pack(pack_id) {
  filters.pack_id = pack_id
  apply_filters()
}

function change_page(step) {
  void update_query({ ...current_query(), offset: Math.max(0, offset.value + step * limit.value) })
}

function change_page_size(size) {
  void update_query({ ...current_query(), limit: size, offset: 0 })
}

function close_preview() {
  preview_version += 1
  preview_open.value = false
  preview_loading.value = false
  preview_item.value = null
  preview_error.value = null
  if (preview_url.value) URL.revokeObjectURL(preview_url.value)
  preview_url.value = ''
}

async function open_preview(item) {
  close_preview()
  const version = preview_version
  preview_item.value = item
  preview_open.value = true
  preview_loading.value = true
  try {
    const content = await getNovelMediaContent(item.kind, item.asset_id)
    if (version !== preview_version) return
    preview_url.value = URL.createObjectURL(content)
  } catch (request_error) {
    if (version === preview_version) preview_error.value = apiError(request_error)
  } finally {
    if (version === preview_version) preview_loading.value = false
  }
}

function playback_error(event) {
  const source = event.currentTarget?.currentSrc || event.currentTarget?.src
  if (!preview_open.value || !preview_url.value || source !== preview_url.value) return
  preview_error.value = { text: tr('preview.playbackFailed'), title: '' }
}

function provider_label(provider) {
  if (!provider) return tr('unavailable')
  const key = `novelMedia.providers.${provider}`
  return te(key) ? t(key) : provider
}

function date_time(value) {
  if (!value) return tr('unavailable')
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return tr('unavailable')
  return date.toLocaleString(locale.value, { dateStyle: 'short', timeStyle: 'short' })
}

function file_size(value) {
  if (value === null || value === undefined) return tr('unavailable')
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte']
  const index = Math.min(3, Math.floor(Math.log10(Math.max(1, value)) / 3))
  return new Intl.NumberFormat(locale.value, { style: 'unit', unit: units[index], unitDisplay: 'short', maximumFractionDigits: 1 }).format(value / 1000 ** index)
}

function duration(value) {
  if (value === null || value === undefined) return tr('unavailable')
  return new Intl.NumberFormat(locale.value, { style: 'unit', unit: 'second', unitDisplay: 'short', maximumFractionDigits: 1 }).format(value / 1000)
}

onBeforeUnmount(() => {
  list_version += 1
  close_preview()
})
</script>

<template>
  <div class="novel-media vga-page" data-testid="novel-media-page" data-insp-path="apps/visual-game-admin/src/pages/novel/Media.vue">
    <PageHeader :title="tr('title')">
      <template #actions>
        <Button variant="secondary" size="sm" :loading="loading" data-testid="novel-media-refresh" @click="load">
          <RefreshCw :size="14" aria-hidden="true" /> {{ tr('refresh') }}
        </Button>
      </template>
    </PageHeader>
    <p class="vga-page-description">{{ tr('description') }}</p>

    <Section>
      <form class="novel-media__filters" data-testid="novel-media-filters" @submit.prevent="apply_filters">
        <label class="novel-media__field" data-testid="novel-media-kind">
          <span class="novel-media__label">{{ tr('filters.kind') }}</span>
          <Select v-model="filters.kind" :options="kind_options" :aria-label="tr('filters.kind')" size="sm" block />
        </label>
        <div class="novel-media__field">
          <label for="novel-media-pack">{{ tr('filters.story') }}</label>
          <Input id="novel-media-pack" v-model="filters.pack_id" size="sm" :placeholder="tr('filters.storyPlaceholder')" data-testid="novel-media-pack" />
        </div>
        <div class="novel-media__field">
          <label for="novel-media-run">{{ tr('filters.run') }}</label>
          <Input id="novel-media-run" v-model="filters.run_id" size="sm" :placeholder="tr('filters.runPlaceholder')" data-testid="novel-media-run" />
        </div>
        <div class="novel-media__field">
          <label for="novel-media-user">{{ tr('filters.user') }}</label>
          <Input id="novel-media-user" v-model="filters.user_id" size="sm" :placeholder="tr('filters.userPlaceholder')" data-testid="novel-media-user" />
        </div>
        <div class="novel-media__filter-actions">
          <Button type="submit" size="sm" :loading="loading" data-testid="novel-media-apply"><Search :size="14" aria-hidden="true" /> {{ tr('filters.apply') }}</Button>
          <Button variant="ghost" size="sm" :disabled="loading" data-testid="novel-media-reset" @click="reset_filters"><RotateCcw :size="14" aria-hidden="true" /> {{ tr('filters.reset') }}</Button>
        </div>
      </form>
    </Section>

    <div v-if="loading" class="vga-state" role="status"><Spinner /><span>{{ tr('loading') }}</span></div>
    <div v-else-if="error" class="vga-state" role="alert">
      <p class="vga-error" :title="error.title">{{ error.text }}</p>
      <Button variant="secondary" data-testid="novel-media-retry" @click="load">{{ tr('retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState v-if="items.length === 0" :title="tr('empty.title')" :description="tr('empty.description')" data-testid="novel-media-empty" />
        <Table v-else :columns="columns" :rows="items" row-key="row_id" data-testid="novel-media-table">
          <template #kind="{ row }">
            <div class="novel-media__stack">
              <Badge :variant="row.kind === 'drama_video' ? 'info' : 'neutral'" size="sm">{{ tr(`kinds.${row.kind}`) }}</Badge>
              <span class="novel-media__muted">{{ provider_label(row.provider) }}</span>
            </div>
          </template>
          <template #pack="{ row }">
            <div class="novel-media__stack">
              <button v-if="row.pack_id" type="button" class="novel-media__link" :title="tr('filters.byStory')" @click="select_pack(row.pack_id)">{{ row.pack_title || row.pack_id }}</button>
              <span v-else>{{ tr('unavailable') }}</span>
              <span v-if="row.pack_id" class="novel-media__identifier">{{ row.pack_id }}</span>
            </div>
          </template>
          <template #owner="{ row }">
            <div class="novel-media__stack">
              <RouterLink v-if="row.user_id" class="novel-media__link" :to="{ name: 'user-detail', params: { id: row.user_id } }">{{ row.user_name || row.user_id }}</RouterLink>
              <span v-else class="novel-media__muted">{{ row.kind === 'opening_speech' ? tr('shared') : tr('unavailable') }}</span>
              <RouterLink v-if="row.run_id" class="novel-media__link novel-media__identifier" :title="tr('openRun')" :to="{ name: 'novel-run-audit', params: { runId: row.run_id } }">{{ row.run_id }}</RouterLink>
            </div>
          </template>
          <template #segment="{ row }"><div class="novel-media__stack"><span>{{ row.segment_id || tr('unavailable') }}</span><span class="novel-media__muted">{{ row.play_language || tr('unavailable') }}</span></div></template>
          <template #created_at="{ row }">{{ date_time(row.created_at) }}</template>
          <template #size="{ row }"><div class="novel-media__stack"><span>{{ file_size(row.bytes) }}</span><span class="novel-media__muted">{{ duration(row.duration_ms) }}</span></div></template>
          <template #actions="{ row }"><Button variant="secondary" size="sm" :data-testid="`novel-media-preview-${row.kind}-${row.asset_id}`" @click="open_preview(row)"><Play :size="14" aria-hidden="true" /> {{ tr('preview.action') }}</Button></template>
        </Table>
      </Section>
      <Pagination
        :has-prev="offset > 0" :has-next="offset + limit < total" :loading="loading" :summary="summary"
        :prev-label="tr('pager.prev')" :next-label="tr('pager.next')" :page-size="limit" :page-size-options="[20, 50, 100]" :page-size-label="tr('pager.pageSize')"
        @prev="change_page(-1)" @next="change_page(1)" @update:page-size="change_page_size"
      />
    </template>

    <Modal :open="preview_open" :title="tr('preview.title')" width="880px" @update:open="!$event && close_preview()">
      <div v-if="preview_item" class="novel-media__preview" data-testid="novel-media-preview">
        <p class="novel-media__preview-heading">{{ preview_item.pack_title || preview_item.pack_id || tr('unavailable') }} · {{ tr(`kinds.${preview_item.kind}`) }}</p>
        <div v-if="preview_loading" class="vga-state" role="status"><Spinner /><span>{{ tr('preview.loading') }}</span></div>
        <div v-else-if="preview_error" class="vga-state" role="alert">
          <p class="vga-error" :title="preview_error.title">{{ preview_error.text }}</p>
          <Button variant="secondary" data-testid="novel-media-preview-retry" @click="open_preview(preview_item)">{{ tr('retry') }}</Button>
        </div>
        <div v-else-if="preview_url" class="novel-media__player">
          <video v-if="preview_item.kind === 'drama_video'" :key="preview_url" :src="preview_url" controls playsinline preload="metadata" :aria-label="tr('preview.video')" data-testid="novel-media-video" @error="playback_error" />
          <audio v-else :key="preview_url" :src="preview_url" controls preload="metadata" :aria-label="tr('preview.audio')" data-testid="novel-media-audio" @error="playback_error" />
        </div>
        <dl class="novel-media__details">
          <div><dt>{{ tr('details.asset') }}</dt><dd>{{ preview_item.asset_id }}</dd></div>
          <div><dt>{{ tr('filters.story') }}</dt><dd>{{ preview_item.pack_id || tr('unavailable') }}</dd></div>
          <div><dt>{{ tr('filters.user') }}</dt><dd>{{ preview_item.user_id || (preview_item.kind === 'opening_speech' ? tr('shared') : tr('unavailable')) }}</dd></div>
          <div><dt>{{ tr('filters.run') }}</dt><dd>{{ preview_item.run_id || tr('unavailable') }}</dd></div>
          <div><dt>{{ tr('details.segment') }}</dt><dd>{{ preview_item.segment_id || tr('unavailable') }}</dd></div>
          <div><dt>{{ tr('details.request') }}</dt><dd>{{ preview_item.request_id || tr('unavailable') }}</dd></div>
          <div><dt>{{ tr('details.language') }}</dt><dd>{{ preview_item.play_language || tr('unavailable') }}</dd></div>
          <div><dt>{{ tr('details.format') }}</dt><dd>{{ preview_item.mime || tr('unavailable') }}</dd></div>
          <div><dt>{{ tr('details.provider') }}</dt><dd>{{ provider_label(preview_item.provider) }}</dd></div>
          <div><dt>{{ tr('details.created') }}</dt><dd>{{ date_time(preview_item.created_at) }}</dd></div>
          <div><dt>{{ tr('details.size') }}</dt><dd>{{ file_size(preview_item.bytes) }}</dd></div>
          <div><dt>{{ tr('details.duration') }}</dt><dd>{{ duration(preview_item.duration_ms) }}</dd></div>
        </dl>
      </div>
      <template #footer><Button variant="secondary" data-testid="novel-media-preview-close" @click="close_preview">{{ tr('close') }}</Button></template>
    </Modal>
  </div>
</template>

<style scoped>
.novel-media__filters { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--drama-space-lg); align-items: end; }
.novel-media__field { display: grid; gap: var(--drama-space-sm); min-width: 0; }
.novel-media__field label, .novel-media__label { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.novel-media__filter-actions { grid-column: 1 / -1; display: flex; justify-content: flex-end; gap: var(--drama-space-sm); }
.novel-media__stack { display: grid; gap: var(--drama-space-xs); justify-items: start; min-width: 0; }
.novel-media__muted { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.novel-media__identifier { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); font-family: var(--drama-font-family-mono); overflow-wrap: anywhere; }
.novel-media__link { display: inline; max-width: 100%; padding: 0; border: 0; background: transparent; color: var(--drama-color-primary); font: inherit; text-align: left; cursor: pointer; overflow-wrap: anywhere; }
.novel-media__link:hover { text-decoration: underline; }
.novel-media__link:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: var(--drama-space-xs); border-radius: var(--drama-radius-sm); }
.novel-media__preview { display: grid; gap: var(--drama-space-lg); }
.novel-media__preview-heading { margin: 0; font-weight: var(--drama-font-weight-medium); overflow-wrap: anywhere; }
.novel-media__player { display: grid; place-items: center; padding: var(--drama-space-lg); background: var(--drama-color-bg); border-radius: var(--drama-radius-md); }
.novel-media__player audio, .novel-media__player video { display: block; width: 100%; max-height: 50vh; }
.novel-media__details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-lg); margin: 0; }
.novel-media__details div { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.novel-media__details dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.novel-media__details dd { margin: 0; overflow-wrap: anywhere; font-size: var(--drama-font-size-sm); }
@media (max-width: 1100px) { .novel-media__filters { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) { .novel-media__filters, .novel-media__details { grid-template-columns: minmax(0, 1fr); } }
</style>
